import React, { useRef, useState } from 'react';
import Papa from 'papaparse';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../../../firebase';
import {
  AlertCircle, CheckCircle2, ChevronDown, Database,
  FileText, Loader2, Upload, X,
} from 'lucide-react';

// ─── helpers ──────────────────────────────────────────────────────────────────

function getISOWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

function normalizeFuelRecord(raw) {
  const fechaCorte = String(raw.fechaCorte || raw.fecha || '').trim();
  const date = new Date(`${fechaCorte}T12:00:00`);
  return {
    anio: Number(raw.anio) || date.getFullYear(),
    semana: Number(raw.semana) || getISOWeek(date),
    fechaCorte,
    diaCorte: raw.diaCorte || 'viernes',
    pais: raw.pais || 'CL',
    region: raw.region || 'Chile',
    comuna: raw.comuna || null,
    bencina93: Number(raw.bencina93 || raw['93'] || raw.gasolina93 || 0),
    bencina95: Number(raw.bencina95 || raw['95'] || raw.gasolina95 || 0),
    bencina97: Number(raw.bencina97 || raw['97'] || raw.gasolina97 || 0),
    diesel: Number(raw.diesel || raw['diésel'] || raw.petroleo || 0),
    estado: String(raw.estado || 'base'),
    fuente: String(raw.fuente || 'manual'),
    fuenteNombre: String(raw.fuenteNombre || 'Tabla base KawésCar'),
    fuenteUrl: String(raw.fuenteUrl || ''),
  };
}

// ─── restriction calendar helpers ─────────────────────────────────────────────

function parseDigitList(raw) {
  if (Array.isArray(raw)) return raw.map(String);
  if (!raw) return [];
  return String(raw).split(/[,;\s]+/).map(s => s.trim()).filter(s => /^\d$/.test(s));
}

function normalizeRestrictionRecord(raw) {
  const fecha = String(raw.fecha || '').trim();
  return {
    fecha,
    anio: Number(raw.anio) || parseInt(fecha.slice(0, 4), 10) || new Date().getFullYear(),
    pais: String(raw.pais || 'CL').trim(),
    region: String(raw.region || 'Metropolitana').trim(),
    zonaClave: String(raw.zonaClave || 'rm_anillo_americo_vespucio').trim(),
    zonaDescripcion: String(raw.zonaDescripcion || 'Interior del anillo Américo Vespucio').trim(),
    categoria: String(raw.categoria || 'sin_sello_verde').trim(),
    digitosRestringidos: parseDigitList(raw.digitosRestringidos),
    horarioInicio: String(raw.horarioInicio || '07:30').trim(),
    horarioFin: String(raw.horarioFin || '21:00').trim(),
    aplica: raw.aplica === 'false' || raw.aplica === false ? false : true,
    esFestivo: raw.esFestivo === 'true' || raw.esFestivo === true,
    fuenteNombre: String(raw.fuenteNombre || '').trim(),
    fuenteUrl: String(raw.fuenteUrl || '').trim(),
    estado: String(raw.estado || 'base').trim(),
  };
}

function validateRestrictionRecord(r, idx) {
  const errors = [];
  if (!r.fecha || !/^\d{4}-\d{2}-\d{2}$/.test(r.fecha))
    errors.push(`Fila ${idx + 1}: fecha inválida ("${r.fecha || ''}")`);
  if (!r.categoria)
    errors.push(`Fila ${idx + 1}: categoría requerida`);
  if (!r.digitosRestringidos || r.digitosRestringidos.length === 0)
    errors.push(`Fila ${idx + 1}: digitosRestringidos vacío o inválido`);
  return errors;
}

function validateFuelRecord(r, idx) {
  const errors = [];
  if (!r.fechaCorte || !/^\d{4}-\d{2}-\d{2}$/.test(r.fechaCorte))
    errors.push(`Fila ${idx + 1}: fechaCorte inválida ("${r.fechaCorte || ''}")`);
  ['bencina93', 'bencina95', 'bencina97', 'diesel'].forEach(f => {
    if (!r[f] || r[f] <= 0)
      errors.push(`Fila ${idx + 1}: ${f} debe ser número > 0 (valor: ${r[f]})`);
  });
  return errors;
}

async function parseCsv(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: r => resolve(r.data),
      error: reject,
    });
  });
}

async function parseJson(file) {
  const text = await file.text();
  const parsed = JSON.parse(text);
  if (Array.isArray(parsed)) return parsed;
  if (Array.isArray(parsed.data)) return parsed.data;
  if (Array.isArray(parsed.records)) return parsed.records;
  throw new Error('El JSON debe ser un array o tener clave "data" / "records".');
}

// ─── config tipos ─────────────────────────────────────────────────────────────

const LOAD_TYPES = [
  {
    id: 'fuel_prices',
    label: 'Precios de combustible semanal',
    collection: 'precios_combustible_semanal',
    callable: 'importWeeklyFuelPrices',
    normalize: normalizeFuelRecord,
    validate: validateFuelRecord,
    previewCols: [
      { key: 'fechaCorte', head: 'Fecha corte' },
      { key: 'semana',     head: 'Sem.' },
      { key: 'bencina93',  head: 'B93' },
      { key: 'bencina95',  head: 'B95' },
      { key: 'bencina97',  head: 'B97' },
      { key: 'diesel',     head: 'Diésel' },
      { key: 'estado',     head: 'Estado' },
      { key: 'fuente',     head: 'Fuente' },
      { key: 'region',     head: 'Región' },
    ],
  },
  {
    id: 'restriction_calendar',
    label: 'Calendario restricción vehicular',
    collection: 'restricciones_vehiculares_calendario',
    callable: 'importRestrictionCalendar',
    normalize: normalizeRestrictionRecord,
    validate: validateRestrictionRecord,
    previewCols: [
      { key: 'fecha',               head: 'Fecha' },
      { key: 'region',              head: 'Región' },
      { key: 'categoria',           head: 'Categoría' },
      { key: 'digitosRestringidos', head: 'Dígitos', render: v => Array.isArray(v) ? v.join(', ') : v },
      { key: 'horarioInicio',       head: 'Inicio' },
      { key: 'horarioFin',          head: 'Fin' },
      { key: 'aplica',              head: 'Aplica', render: v => v ? 'Sí' : 'No' },
      { key: 'estado',              head: 'Estado' },
    ],
  },
  // Tipos futuros: marcas, empresas, tips, planes, FAQ — agregar aquí
];

// ─── component ────────────────────────────────────────────────────────────────

export default function CargaDatosArchivo() {
  const fileInputRef = useRef(null);

  const [tipoId, setTipoId]           = useState(LOAD_TYPES[0].id);
  const [fileName, setFileName]       = useState('');
  const [rawFile, setRawFile]         = useState(null);
  const [records, setRecords]         = useState([]);    // normalized
  const [rowErrors, setRowErrors]     = useState([]);    // validation errors per row
  const [parseError, setParseError]   = useState('');
  const [analyzed, setAnalyzed]       = useState(false);
  const [onlyValid, setOnlyValid]     = useState(false);
  const [loading, setLoading]         = useState(false);
  const [result, setResult]           = useState(null);  // { ok, created, updated, total } | { error }

  const tipo = LOAD_TYPES.find(t => t.id === tipoId) || LOAD_TYPES[0];

  const validRecords  = records.filter((_, i) => !rowErrors.some(e => e.startsWith(`Fila ${i + 1}:`)));
  const invalidCount  = records.length - validRecords.length;

  // ── reset ──────────────────────────────────────────────────────────────────
  function reset() {
    setFileName('');
    setRawFile(null);
    setRecords([]);
    setRowErrors([]);
    setParseError('');
    setAnalyzed(false);
    setOnlyValid(false);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  // ── file selection ─────────────────────────────────────────────────────────
  function handleFileSelected(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    reset();
    setFileName(file.name);
    setRawFile(file);
    setAnalyzed(false);
  }

  // ── analyze ────────────────────────────────────────────────────────────────
  async function handleAnalyze() {
    if (!rawFile) return;
    setLoading(true);
    setParseError('');
    setResult(null);
    try {
      let raw;
      const ext = rawFile.name.split('.').pop().toLowerCase();
      if (ext === 'json') raw = await parseJson(rawFile);
      else raw = await parseCsv(rawFile);

      if (!Array.isArray(raw) || raw.length === 0) {
        setParseError('El archivo está vacío o no tiene filas.');
        setLoading(false);
        return;
      }

      const normalized = raw.map(tipo.normalize);
      const errors = normalized.flatMap((r, i) => tipo.validate(r, i));
      setRecords(normalized);
      setRowErrors(errors);
      setAnalyzed(true);
    } catch (err) {
      setParseError(`Error al leer el archivo: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  // ── load ───────────────────────────────────────────────────────────────────
  async function handleLoad() {
    const toUpload = onlyValid ? validRecords : records;
    if (!toUpload.length) return;
    setLoading(true);
    setResult(null);
    try {
      const functions = getFunctions(app, 'us-central1');
      const fn = httpsCallable(functions, tipo.callable);
      const res = await fn({ records: toUpload });
      setResult(res.data);
    } catch (err) {
      setResult({ error: err.message || 'Error desconocido al cargar datos.' });
    } finally {
      setLoading(false);
    }
  }

  // ── can load ───────────────────────────────────────────────────────────────
  const canLoad = analyzed && !loading && (
    rowErrors.length === 0 || (onlyValid && validRecords.length > 0)
  );

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">

      {/* Header */}
      <div className="px-6 py-5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 border border-blue-100">
            <Database className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Carga de datos desde archivo</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Sube archivos CSV o JSON para poblar colecciones de contenido de KawésCar.
            </p>
          </div>
        </div>
      </div>

      <div className="px-6 py-5 space-y-5">

        {/* ── Tipo de carga ── */}
        <div className="space-y-1.5">
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-500">
            Tipo de contenido
          </label>
          <div className="relative">
            <select
              value={tipoId}
              onChange={e => { reset(); setTipoId(e.target.value); }}
              className="w-full appearance-none rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 pr-10 text-sm font-medium text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              {LOAD_TYPES.map(t => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          </div>
          <p className="text-[11px] text-slate-400">
            Colección destino: <span className="font-mono font-semibold text-slate-600">{tipo.collection}</span>
          </p>
        </div>

        {/* ── Dropzone ── */}
        <div className="space-y-1.5">
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-500">
            Archivo
          </label>
          <div
            onClick={() => fileInputRef.current?.click()}
            className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 px-6 py-8 cursor-pointer hover:border-blue-400 hover:bg-blue-50/40 transition"
          >
            <Upload className="h-8 w-8 text-slate-300" />
            <p className="text-sm text-slate-500 text-center">
              Arrastra un archivo CSV o JSON aquí, o{' '}
              <span className="text-blue-600 font-semibold">haz click para seleccionar</span>
            </p>
            <p className="text-[11px] text-slate-400">.csv · .json</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.json,application/json,text/csv"
            onChange={handleFileSelected}
            className="hidden"
          />
          {fileName && (
            <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="h-4 w-4 text-blue-500 shrink-0" />
                <span className="text-sm font-medium text-slate-700 truncate">{fileName}</span>
              </div>
              <button type="button" onClick={reset} className="ml-2 shrink-0 text-slate-400 hover:text-red-500 transition">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {/* ── Actions row ── */}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleAnalyze}
            disabled={!rawFile || loading}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-800 px-4 py-2 text-sm font-bold text-white hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {loading && !analyzed ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            Analizar archivo
          </button>
          <button
            type="button"
            onClick={reset}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition"
          >
            <X className="h-4 w-4" />
            Limpiar
          </button>
        </div>

        {/* ── Parse error ── */}
        {parseError && (
          <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
            <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
            <p className="text-sm text-red-700">{parseError}</p>
          </div>
        )}

        {/* ── Analysis result ── */}
        {analyzed && records.length > 0 && (
          <div className="space-y-4">

            {/* Summary pills */}
            <div className="flex flex-wrap gap-2 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
              <Pill color="blue"   label="Detectados"    value={records.length} />
              <Pill color="green"  label="Válidos"       value={validRecords.length} />
              <Pill color={invalidCount > 0 ? 'red' : 'green'} label="Con errores" value={invalidCount} />
              <Pill color="slate"  label="Colección"     value={tipo.collection} mono />
            </div>

            {/* Row errors */}
            {rowErrors.length > 0 && (
              <details className="rounded-xl border border-amber-200 bg-amber-50">
                <summary className="flex cursor-pointer items-center gap-2 px-4 py-2.5 text-sm font-bold text-amber-700">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {rowErrors.length} error{rowErrors.length !== 1 ? 'es' : ''} de validación
                </summary>
                <ul className="px-4 pb-3 space-y-0.5">
                  {rowErrors.map((e, i) => (
                    <li key={i} className="text-xs text-amber-800 font-mono">{e}</li>
                  ))}
                </ul>
              </details>
            )}

            {/* Preview table */}
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">
                Vista previa (máx. 10 filas)
              </p>
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      {tipo.previewCols.map(c => (
                        <th key={c.key} className="px-3 py-2 text-left font-bold text-slate-500 whitespace-nowrap">
                          {c.head}
                        </th>
                      ))}
                      <th className="px-3 py-2 text-left font-bold text-slate-500">Estado fila</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {records.slice(0, 10).map((r, i) => {
                      const hasErr = rowErrors.some(e => e.startsWith(`Fila ${i + 1}:`));
                      return (
                        <tr key={i} className={hasErr ? 'bg-red-50' : 'hover:bg-slate-50'}>
                          {tipo.previewCols.map(c => (
                            <td key={c.key} className="px-3 py-1.5 text-slate-700 whitespace-nowrap font-mono">
                              {c.render ? c.render(r[c.key]) : (r[c.key] ?? '—')}
                            </td>
                          ))}
                          <td className="px-3 py-1.5">
                            {hasErr
                              ? <span className="text-red-500 font-bold">✗ Error</span>
                              : <span className="text-emerald-500 font-bold">✓ OK</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {records.length > 10 && (
                  <p className="px-3 py-2 text-[11px] text-slate-400 border-t border-slate-100">
                    … y {records.length - 10} fila{records.length - 10 !== 1 ? 's' : ''} más
                  </p>
                )}
              </div>
            </div>

            {/* Only-valid checkbox */}
            {invalidCount > 0 && (
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={onlyValid}
                  onChange={e => setOnlyValid(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 accent-blue-600"
                />
                <span className="text-sm text-slate-700">
                  Cargar solo los <span className="font-bold text-emerald-600">{validRecords.length}</span> registros válidos
                  (ignorar los {invalidCount} con errores)
                </span>
              </label>
            )}

            {/* Load button */}
            <button
              type="button"
              onClick={handleLoad}
              disabled={!canLoad}
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm shadow-blue-500/25 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              {loading
                ? <><Loader2 className="h-4 w-4 animate-spin" />Cargando…</>
                : <><Upload className="h-4 w-4" />Cargar información</>}
            </button>
          </div>
        )}

        {/* ── Result ── */}
        {result && (
          result.error ? (
            <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-bold text-red-700">Error al cargar</p>
                <p className="text-sm text-red-600 mt-0.5">{result.error}</p>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-4 space-y-1">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                <p className="text-sm font-bold text-emerald-800">Carga completada</p>
              </div>
              <p className="text-sm text-emerald-700">
                <span className="font-bold">{result.total}</span> registro{result.total !== 1 ? 's' : ''} procesados ·{' '}
                <span className="font-bold">{result.created}</span> nuevo{result.created !== 1 ? 's' : ''} ·{' '}
                <span className="font-bold">{result.updated}</span> actualizado{result.updated !== 1 ? 's' : ''}
              </p>
              <p className="text-xs text-emerald-600 font-mono">Colección: {tipo.collection}</p>
            </div>
          )
        )}

      </div>
    </div>
  );
}

function Pill({ color, label, value, mono }) {
  const colors = {
    blue:  'bg-blue-50  text-blue-700  border-blue-100',
    green: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    red:   'bg-red-50   text-red-700   border-red-100',
    slate: 'bg-slate-100 text-slate-600  border-slate-200',
  };
  return (
    <div className={`inline-flex items-baseline gap-1.5 rounded-lg border px-2.5 py-1 text-xs ${colors[color] || colors.slate}`}>
      <span className="text-[10px] font-bold uppercase tracking-wide opacity-70">{label}</span>
      <span className={`font-black ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}
